import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { ChevronLeft, RotateCcw, Star } from "lucide-react";
import { api } from "@/lib/api";
import { Loader } from "./Common";

const BLANKS = [0.3, 0.6, 1];

function shuffle<T>(a: T[], seed: number) { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = (seed * (i + 7)) % (i + 1); [b[i], b[j]] = [b[j], b[i]]; } return b; }

export function MemoryVerseGame({ kidId }: { kidId: string }) {
  const [d, setD] = useState<any>(null);
  const [level, setLevel] = useState(1);
  const [filled, setFilled] = useState<Record<number, number>>({});
  const [pick, setPick] = useState<number | null>(null);
  const [wrong, setWrong] = useState<number | null>(null);
  const [won, setWon] = useState(false);
  useEffect(() => { api.get(`/kids/${kidId}/memory`).then((r) => { setD(r.data); setLevel(Math.min(3, (r.data.levels_done.length || 0) + 1)); }); }, [kidId]);
  const words = useMemo(() => (d?.text || "").split(/\s+/).filter(Boolean), [d]);
  const blanks = useMemo(() => {
    if (!words.length) return [] as number[];
    const k = Math.max(1, Math.round(words.length * BLANKS[level - 1]));
    const order = shuffle(words.map((_: string, i: number) => i), words.length + level * 3);
    return order.slice(0, k).sort((a: number, b: number) => a - b);
  }, [words, level]);
  const bank = useMemo(() => shuffle(blanks, level * 11 + 5), [blanks, level]);
  useEffect(() => { setFilled({}); setPick(null); setWon(false); }, [level, d?.text]);
  if (!d) return <Loader />;
  const place = async (slot: number, wordIdx: number) => {
    if (filled[slot] !== undefined) return;
    if (words[wordIdx].replace(/\W/g, "").toLowerCase() !== words[slot].replace(/\W/g, "").toLowerCase()) { setWrong(slot); setTimeout(() => setWrong(null), 500); return; }
    const next = { ...filled, [slot]: wordIdx };
    setFilled(next); setPick(null);
    if (Object.keys(next).length === blanks.length) {
      setWon(true);
      confetti({ particleCount: 160, spread: 110, origin: { y: 0.5 }, colors: ["#FDE68A", "#81B29A", "#F4A3B8", "#FFFFFF"], disableForReducedMotion: true });
      api.post(`/kids/${kidId}/memory`, { level }).then((r) => setD((x: any) => ({ ...x, levels_done: r.data.levels_done }))).catch(() => {});
    }
  };
  const used = new Set(Object.values(filled));
  return (
    <div data-testid="memory-verse-game">
      <Link to="/kids" className="mb-6 inline-flex items-center gap-1 text-lg font-semibold text-slate-600" data-testid="memory-back"><ChevronLeft className="h-5 w-5" />Back</Link>
      <h1 className="text-4xl font-semibold text-slate-800">Memory verse of the week</h1>
      <div className="mt-4 flex gap-2" data-testid="memory-levels">
        {[1, 2, 3].map((l) => (
          <button key={l} onClick={() => setLevel(l)} data-testid={`memory-level-${l}`} aria-pressed={level === l}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-lg font-semibold transition-colors ${level === l ? "bg-[#81B29A] text-white" : "bg-white text-slate-600 ring-2 ring-[#E2DBCE]"}`}>
            <Star className={`h-5 w-5 ${d.levels_done.includes(l) ? "fill-amber-300 text-amber-400" : ""}`} />Level {l}
          </button>
        ))}
      </div>
      <div className="mt-8 rounded-[2rem] bg-white p-6 text-2xl leading-[2.6] shadow-lg ring-4 ring-amber-100 sm:p-10 sm:text-3xl" data-testid="memory-verse">
        {words.map((w: string, i: number) => blanks.includes(i) ? (
          <motion.button key={i} data-testid={`memory-slot-${i}`} animate={wrong === i ? { x: [0, -8, 8, -6, 6, 0] } : {}} transition={{ duration: 0.4 }}
            onClick={() => pick !== null && place(i, pick)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => place(i, Number(e.dataTransfer.getData("text")))}
            className={`mx-1 inline-block min-w-[4.5rem] rounded-xl border-b-4 px-2 align-middle leading-tight ${filled[i] !== undefined ? "border-[#81B29A] bg-[#EAF5EE] py-1 text-[#2F6B4B]" : `border-dashed py-3 ${pick !== null ? "border-amber-400 bg-amber-50" : "border-slate-300 bg-slate-50"}`}`}>
            {filled[i] !== undefined ? words[filled[i]] : ""}
          </motion.button>
        ) : <span key={i} className="mx-1 text-slate-800">{w}</span>)}
        <p className="mt-2 text-lg font-semibold text-[#81B29A]">{d.ref}</p>
      </div>
      {!won ? (
        <div className="mt-8 flex flex-wrap gap-3" data-testid="memory-bank">
          {bank.map((wi: number) => !used.has(wi) && (
            <motion.button key={wi} layout draggable onDragStart={(e: any) => e.dataTransfer.setData("text", String(wi))} onClick={() => setPick(pick === wi ? null : wi)}
              data-testid={`memory-word-${wi}`} whileTap={{ scale: 0.92 }}
              className={`cursor-grab rounded-2xl px-5 py-3 text-2xl font-semibold shadow-md transition-colors ${pick === wi ? "bg-amber-300 text-amber-950 ring-4 ring-amber-200" : "bg-[#FFF3D6] text-slate-800 hover:bg-amber-100"}`}>
              {words[wi].replace(/[.,;:!?]+$/, "")}
            </motion.button>
          ))}
          <p className="w-full text-base text-slate-500">Tap a word, then tap its space. Or drag it into place!</p>
        </div>
      ) : (
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-8 rounded-[2rem] bg-[#81B29A] p-8 text-center text-white" data-testid="memory-won">
          <p className="text-4xl font-semibold">Amazing! Level {level} done!</p>
          <div className="mt-5 flex justify-center gap-3">
            {level < 3 && <button onClick={() => setLevel(level + 1)} data-testid="memory-next-level" className="rounded-full bg-white px-6 py-3 text-xl font-semibold text-[#2F6B4B]">Try level {level + 1}</button>}
            <button onClick={() => { setFilled({}); setWon(false); }} data-testid="memory-replay" className="inline-flex items-center gap-2 rounded-full bg-white/20 px-6 py-3 text-xl font-semibold ring-2 ring-white/60"><RotateCcw className="h-5 w-5" />Play again</button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
